import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import * as Yup from "yup";

import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TextField as MuiTextField,
  TableRow,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Delete, Edit, Search } from "@mui/icons-material";
import useAppSelector from "../../hooks/useAppSelector";
import useAppDispatch from "../../hooks/useAppDispatch";
import {
  createAssociation,
  deleteAssociation,
  fetchAssociations,
  openAssociationForm,
  setAssociation,
  updateAssociation,
} from "../../redux/slices/association";
import { Formik } from "formik";
import useAuth from "../../hooks/useAuth";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";
import { fi } from "date-fns/locale";
import { toDateOnlyString } from "../../utils/dateUtils";
import { fetchAllRoads, Road } from "../../redux/slices/road";
import { usePermission } from "../../hooks/usePermission";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Button = styled(MuiButton)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export const fetchCoordinates = async (kuntanimi: string, katunimi: string) => {
  if (!katunimi || !kuntanimi) return null;

  const url = `https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna?kuntanimi=${encodeURIComponent(
    kuntanimi
  )}&katunimi=${encodeURIComponent(
    katunimi
  )}&valihaku=true&palautusarvot=1&palautusarvot=2&palautusarvot=3&palautusarvot=4&palautusarvot=5&palautusarvot=6`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.error("API-virhe:", err);
    return null;
  }
};

export const validateRoad = async (
  road: string,
  municipality: string,
  allRoads: Road[]
) => {
  if (!road || !municipality) return "Tie ja kunta ovat pakollisia";

  if (road.length < 3) {
    return "Tien nimen tulee olla vähintään 3 merkkiä";
  }

  if (municipality.length < 3) {
    return "Tien nimen tulee olla vähintään 3 merkkiä";
  }

  const data = await fetchCoordinates(municipality, road);
  if (!data || !data.features || data.features.length === 0) {
    return "Tie ei löydy annetuilla tiedoilla";
  }

  const virheet = data.features[0]?.properties?.virheet;
  if (virheet && virheet.trim().length > 0) {
    return virheet;
  }

  const duplicate = allRoads.find(
    (r) =>
      r.name.trim().toLowerCase() === road.trim().toLowerCase() &&
      r.municipality.trim().toLowerCase() === municipality.trim().toLowerCase()
  );

  if (duplicate) {
    return "Tie samalla nimellä ja kunnalla löytyy jo tietokannasta.";
  }

  return undefined;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Nimi on pakollinen"),
  email: Yup.string().email("Anna oikea sähköpostiosoite"),
  phone: Yup.string(),
  meetingDate: Yup.date().required("Kokouspäivä on pakollinen"),
  billDueDate: Yup.date().required("Eräpäivä on pakollinen"),
  basePay: Yup.number().min(0).required("Perusmaksu on pakollinen"),
  reminderFee: Yup.number()
    .min(0)
    .max(5, "Maksumuistutus voi olla enintään 5€")
    .required("Maksumuistutuksen hinta on pakollinen"),
  foreclosureFee: Yup.number()
    .min(0)
    .max(51, "Ulosottomaksu voi olla enintään 51€")
    .required("Ulosottomaksu on pakollinen"),
  unitPay: Yup.number().min(0).required("Yksikköhinta on pakollinen"),
  iban: Yup.string()
    .nullable()
    .matches(/^FI\d{2}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{2}$/, "Anna oikea IBAN")
    .required("IBAN on pakollinen"),
  bic: Yup.string()
    .nullable()
    .matches(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, "Anna oikea BIC")
    .required("BIC on pakollinen"),
  road: Yup.string().required("Tienimi on pakollinen"),
  municipality: Yup.string().required("Kunta on pakollinen"),
});

function AssociationForm({
  onComplete,
  setSnackbarMessage,
  setSnackbarSeverity,
  setOpenSnackbar,
  setProgress,
}: {
  onComplete: (success: boolean) => void;
  setSnackbarMessage: (msg: string) => void;
  setSnackbarSeverity: (
    severity: "success" | "error" | "info" | "warning"
  ) => void;
  setOpenSnackbar: (open: boolean) => void;
  setProgress?: (value: number) => void;
}) {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { refreshCurrentUser } = useAuth();
  const { allRoads } = useAppSelector((state) => state.road);
  const [road, setRoad] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState<{
    road: string;
    municipality: string;
  }>({
    road: "",
    municipality: "",
  });
  const [validationError, setValidationError] = useState<string | undefined>();
  const { association } = useAppSelector((state) => state.association);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery({ road, municipality });
    }, 800); // Voit säätää viivettä tarpeen mukaan

    return () => {
      clearTimeout(handler);
    };
  }, [road, municipality]);

  useEffect(() => {
    dispatch(fetchAllRoads());
  }, [dispatch]);

  useEffect(() => {
    async function validate() {
      const { road: debouncedRoad, municipality: debouncedMunicipality } =
        debouncedQuery;

      if (
        debouncedRoad &&
        debouncedRoad.length >= 3 &&
        debouncedMunicipality &&
        debouncedMunicipality.trim().length > 0
      ) {
        const error = await validateRoad(
          debouncedRoad,
          debouncedMunicipality,
          allRoads
        );
        setValidationError(error);
      } else {
        setValidationError(undefined);
      }
    }

    validate();
  }, [debouncedQuery]);

  const handleSubmit = async (
    // eslint-disable-next-line
    values: any,
    // eslint-disable-next-line
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      const payload = {
        ...values,
        meetingDate: toDateOnlyString(values.meetingDate),
        billDueDate: toDateOnlyString(values.billDueDate),
      };
      if (association && association.id !== undefined) {
        await dispatch(updateAssociation(association.id, payload));
      } else {
        await dispatch(createAssociation(payload));
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      // eslint-disable-next-line
      dispatch(openAssociationForm(false));
      onComplete(true);
      await refreshCurrentUser();
    } catch (error: any) {
      const message = error.response?.data?.message || "Tuntematon virhe";
      setStatus({ sent: false });
      setErrors({ submit: message });
      setSubmitting(false);

      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      onComplete(false);
    }
  };

  return (
    <Formik
      initialValues={
        association
          ? {
              name: association.name,
              email: association.email || "",
              phone: association.phone || "",
              meetingDate: association.meetingDate,
              billDueDate: association.billDueDate,
              basePay: association.basePay,
              reminderFee: association.reminderFee,
              road:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id,
              foreclosureFee: association.foreclosureFee,
              unitPay: association.unitPay,
              iban: association.iban,
              bic: association.bic,
              municipality:
                currentUser?.activeAssociation.road.municipality ??
                currentUser?.associations[0].road.municipality,
            }
          : {
              name: "",
              email: "",
              phone: "",
              meetingDate: "",
              billDueDate: "",
              basePay: "",
              reminderFee: "",
              foreclosureFee: "",
              unitPay: "",
              iban: "",
              bic: "",
              road: "",
              municipality: "",
            }
      }
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <Card mb={6}>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={6}>
                {!association && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Tiehen liittyvät tiedot
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                    </Grid>
                    <Grid item md={6}>
                      <TextField
                        name="road"
                        label="Tien nimi"
                        value={values.road}
                        error={Boolean(validationError)}
                        fullWidth
                        helperText={validationError}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setRoad(e.target.value);
                          handleChange(e);
                        }}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6}>
                      <TextField
                        name="municipality"
                        label="Kunta"
                        value={values.municipality}
                        error={Boolean(validationError)}
                        fullWidth
                        helperText={validationError}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setMunicipality(e.target.value);
                          handleChange(e);
                        }}
                        variant="outlined"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    {association
                      ? "Muokkaa tiekunnan tietoja"
                      : "Tiekuntaan liittyvät tiedot"}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="name"
                    label="Nimi"
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="email"
                    label="Sähköposti"
                    value={values.email}
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="phone"
                    label="Puhelinnumero"
                    value={values.phone}
                    error={Boolean(touched.phone && errors.phone)}
                    fullWidth
                    helperText={touched.phone && errors.phone}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item md={6}>
                  {/* @ts-ignore */}
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={fi}
                  >
                    <DatePicker
                      label="Kokous Pvm"
                      value={values.meetingDate}
                      inputFormat="dd.MM.yyyy"
                      //onChange={handleChange}
                      onChange={(newValue) => {
                        setFieldValue("meetingDate", newValue); // This will update Formik's value
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="meetingDate"
                          fullWidth
                          //onBlur={handleBlur}
                          error={Boolean(
                            touched.meetingDate && errors.meetingDate
                          )}
                          //helperText={touched.meetingDate && errors.meetingDate}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item md={6}>
                  {/* @ts-ignore */}
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={fi}
                  >
                    <DatePicker
                      label="Laskun eräpäivä"
                      value={values.billDueDate}
                      inputFormat="dd.MM.yyyy"
                      onChange={(newValue) => {
                        setFieldValue("billDueDate", newValue); // This will update Formik's value
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="billDueDate"
                          fullWidth
                          error={Boolean(
                            touched.billDueDate && errors.billDueDate
                          )}
                          //helperText={touched.billDueDate && errors.billDueDate}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  {/* <TextField
                    name="billDueDate"
                    label="billDueDate"
                    value={values.billDueDate}
                    error={Boolean(touched.billDueDate && errors.billDueDate)}
                    fullWidth
                    helperText={touched.billDueDate && errors.billDueDate}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    my={2}
                  /> */}
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="basePay"
                    label="Perusmaksu (€)"
                    value={values.basePay}
                    error={Boolean(touched.basePay && errors.basePay)}
                    fullWidth
                    helperText={touched.basePay && errors.basePay}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="reminderFee"
                    label="Muistutusmaksu (€)"
                    value={values.reminderFee}
                    error={Boolean(touched.reminderFee && errors.reminderFee)}
                    fullWidth
                    helperText={touched.reminderFee && errors.reminderFee}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="foreclosureFee"
                    label="Ulosottomaksu (€)"
                    value={values.foreclosureFee}
                    error={Boolean(
                      touched.foreclosureFee && errors.foreclosureFee
                    )}
                    fullWidth
                    helperText={touched.foreclosureFee && errors.foreclosureFee}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="unitPay"
                    label="Yksikkömaksu (€)"
                    value={values.unitPay}
                    error={Boolean(touched.unitPay && errors.unitPay)}
                    fullWidth
                    helperText={touched.unitPay && errors.unitPay}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="iban"
                    label="Tilinumero (IBAN)"
                    placeholder="FI1231231231231234"
                    value={values.iban}
                    error={Boolean(touched.iban && errors.iban)}
                    fullWidth
                    helperText={touched.iban && errors.iban}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6}>
                  <TextField
                    name="bic"
                    label="SWIFT-koodi (BIC)"
                    placeholder="NDEAFIHH"
                    value={values.bic}
                    error={Boolean(touched.bic && errors.bic)}
                    fullWidth
                    helperText={touched.bic && errors.bic}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                mt={3}
                mr={2}
              >
                Tallenna
              </Button>
              <Button
                variant="contained"
                color="error"
                mt={3}
                onClick={() => dispatch(openAssociationForm(false))}
              >
                Peruuta
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function AssociationList(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [didFetch, setDidFetch] = useState(false);

  const { status } = useAppSelector((state) => state.association);
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Check permissions using RBAC
  const { hasPermission: canUpdateAssociation } = usePermission(
    "association",
    "update",
    associationId
  );

  useEffect(() => {
    if (!didFetch && status === "idle" && associationId) {
      dispatch(fetchAssociations(associationId));
      setDidFetch(true);
    }
  }, [dispatch, currentUser, status, didFetch, associationId]);

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tiekunta
        </Typography>
        {canUpdateAssociation ? (
          <Button
            variant="contained"
            color="primary"
            size={"small"}
            onClick={() => props.handleEditAssociation(undefined, true)}
          >
            Lisää
          </Button>
        ) : null}
      </CardContent>

      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>Nimi</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (props.rows || []).map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Search style={{ cursor: "pointer" }} onClick={() => {}} />{" "}
                    {(currentUser?.activeAssociation?.id === row.id ||
                      (!currentUser?.activeAssociation &&
                        currentUser?.associations[0]?.id === row.id)) && (
                      <Edit
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleEditAssociation(row, true)}
                      />
                    )}{" "}
                    {currentUser?.isSuperAdmin ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Associations() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { open } = useAppSelector((state) => state.association);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleEditAssociation = (association: any, isOpen: boolean) => {
    dispatch(setAssociation(association));
    dispatch(openAssociationForm(isOpen));
  };

  const handleDelete = async (association: any) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa "${association.name}"?`
    );
    if (confirmed) {
      setProgress(100);
      try {
        await dispatch(deleteAssociation(association.id));
        setSnackbarMessage("Tiekunta poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa tiekuntaa.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    dispatch(fetchAssociations(associationId));

    return () => {
      dispatch(openAssociationForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Tiet" />
      <Typography variant="h3" gutterBottom display="inline">
        Tiekunta
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tiekunta</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {open ? (
        <AssociationForm
          onComplete={(success: boolean) => {
            setProgress?.(100);
            if (success) {
              setSnackbarMessage("Tiekunta tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa tietokuntaa.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
          setOpenSnackbar={setOpenSnackbar}
          setProgress={setProgress}
        />
      ) : (
        <AssociationList
          rows={currentUser?.associations}
          handleEditAssociation={handleEditAssociation}
          handleDelete={handleDelete}
          openForm={open}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Associations;
